// src/theme.js — Refined Field-Material visual refresh (A.6, 2026-05-09)
//
// Token philosophy:
//   - palette.primary.*    : Zoetis orange (#EA5D0D) — IMMUTABLE.
//   - palette.background.* : Off-white warmed surfaces (not pure white).
//   - palette.text.*       : Cool-grey hierarchy (primary/secondary/disabled).
//   - palette.divider      : Warm greige border.
//   - semantic tokens      : success/warning/error/info — hue-aligned, desaturated.
//   - All component overrides use theme.spacing() — no raw px in overrides.
//
// Future dark mode: swap the token VALUES in this file only.
// No component code needs to change — they already use theme tokens.

import { createTheme } from '@mui/material/styles'

// ─── RAW PALETTE VALUES ───────────────────────────────────────────────────────
// Define named constants first so the theme object stays readable.

const ZOETIS_ORANGE = '#EA5D0D'

// ─── BORDER RADIUS SCALE ──────────────────────────────────────────────────────
// Single source of truth for corner rounding across the app (2026-05-10).
// Antes: radii inline arbitrários espalhados pelo código (300px, 400px, 4, 6,
// 8, 999, 50%) sem sistema — gerava inconsistência visual.
// Agora: 5 níveis discretos que cobrem 100% dos casos:
//   xs (4px)   — chips, badges, tags
//   sm (6px)   — base (button, input) — equivale a shape.borderRadius
//   md (8px)   — panels, table container, paginator, search/filter (DEFAULT)
//   lg (12px)  — stand-alone cards
//   xl (16px)  — dialog/modal
//   pill       — buttons que devem ser pílula (PageActions, TopActionBar)
//   circle     — avatars, dots, status indicators
const RADIUS = {
  xs:     4,
  sm:     6,
  md:     8,
  lg:    12,
  xl:    16,
  pill: 999,
  circle: '50%',
}

// Neutrals — warmed off-whites and greiges for surfaces + cool greys for text.
// "Warmed" means a very slight amber tint (≈ 3–5° hue shift toward orange),
// not perceptually visible but makes the orange primary feel at home.
const SURFACE_DEFAULT  = '#FAF8F6'   // app background — warm off-white
const SURFACE_PAPER    = '#FFFFFF'   // card/paper — pure white (contrast on background)
const SURFACE_SUBTLE   = '#F3F1EF'   // subtle inset areas (readonly fields, table zebra)
const BORDER_DEFAULT   = '#E0DDD9'   // dividers and outlined borders — warm greige
const BORDER_STRONG    = '#C8C5C0'   // stronger border (focus rings, table head)

// Text hierarchy — cool greys (desaturated, no warm tint on text)
const TEXT_PRIMARY     = '#1A1A1A'   // near-black — body text, labels
const TEXT_SECONDARY   = '#5C5C5C'   // secondary — helper text, captions
const TEXT_DISABLED    = '#A0A0A0'   // disabled
const TEXT_HINT        = '#767676'   // hints, placeholders

// Semantic tokens — hue-aligned with orange but desaturated for reliability
// (green-teal for success, amber for warning, deep red for error, blue-steel for info)
const SEMANTIC_SUCCESS_MAIN  = '#2E7D50'  // forest green
const SEMANTIC_SUCCESS_LIGHT = '#E8F5EE'
const SEMANTIC_WARNING_MAIN  = '#B45309'  // amber-brown (not neon yellow)
const SEMANTIC_WARNING_LIGHT = '#FEF3E2'
const SEMANTIC_ERROR_MAIN    = '#C0392B'  // deep red
const SEMANTIC_ERROR_LIGHT   = '#FDEDEB'
const SEMANTIC_INFO_MAIN     = '#1D5FA4'  // blue-steel
const SEMANTIC_INFO_LIGHT    = '#E8F1FB'

// ─── THEME ────────────────────────────────────────────────────────────────────

const appTheme = createTheme({
  // ── Palette ────────────────────────────────────────────────────────────────
  palette: {
    mode: 'light',
    primary: {
      main:         ZOETIS_ORANGE,
      light:        '#FF8040',   // lighter hover state
      dark:         '#C44A09',   // pressed state
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         '#5C5C5C',
      light:        '#888888',
      dark:         '#3A3A3A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: SURFACE_DEFAULT,
      paper:   SURFACE_PAPER,
    },
    text: {
      primary:   TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
      disabled:  TEXT_DISABLED,
    },
    divider: BORDER_DEFAULT,
    action: {
      // MUI uses these for hover/selected/disabled states on interactive elements
      hover:           'rgba(234, 93, 13, 0.06)',
      selected:        'rgba(234, 93, 13, 0.12)',
      disabledBackground: '#EBEBEB',
      disabled:        TEXT_DISABLED,
    },
    success: {
      main:         SEMANTIC_SUCCESS_MAIN,
      light:        SEMANTIC_SUCCESS_LIGHT,
      contrastText: '#FFFFFF',
    },
    warning: {
      main:         SEMANTIC_WARNING_MAIN,
      light:        SEMANTIC_WARNING_LIGHT,
      contrastText: '#FFFFFF',
    },
    error: {
      main:         SEMANTIC_ERROR_MAIN,
      light:        SEMANTIC_ERROR_LIGHT,
      contrastText: '#FFFFFF',
    },
    info: {
      main:         SEMANTIC_INFO_MAIN,
      light:        SEMANTIC_INFO_LIGHT,
      contrastText: '#FFFFFF',
    },
  },

  // ── Typography ─────────────────────────────────────────────────────────────
  // IBM Plex Sans for display (H1-H3), Roboto for body.
  // font-feature-settings: 'cv11' = alternate digit 1, 'ss01' = alternate 'a',
  // 'tnum' = tabular numbers (important for KPI dashboards and tables).
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),

    // Heading scale: IBM Plex Sans
    h1: {
      fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
      fontWeight: 700,
      fontSize:   '2rem',       // 32px
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
      fontFeatureSettings: '"cv11", "ss01"',
    },
    h2: {
      fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
      fontWeight: 700,
      fontSize:   '1.5rem',     // 24px
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      fontFeatureSettings: '"cv11", "ss01"',
    },
    h3: {
      fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
      fontWeight: 600,
      fontSize:   '1.25rem',    // 20px
      lineHeight: 1.35,
      letterSpacing: '-0.01em',
      fontFeatureSettings: '"cv11", "ss01"',
    },
    h4: {
      fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
      fontWeight: 600,
      fontSize:   '1.125rem',   // 18px
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
      fontWeight: 500,
      fontSize:   '1rem',       // 16px
      lineHeight: 1.45,
    },
    h6: {
      fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
      fontWeight: 500,
      fontSize:   '0.875rem',   // 14px
      lineHeight: 1.5,
    },

    // Body: Roboto (already loaded)
    body1: {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize:   '1rem',       // 16px
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize:   '0.875rem',   // 14px
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize:   '1rem',
      lineHeight: 1.45,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize:   '0.875rem',
      lineHeight: 1.45,
    },
    button: {
      fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
      fontWeight: 600,
      fontSize:   '0.875rem',   // 14px
      lineHeight: 1,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    },
    caption: {
      fontWeight: 400,
      fontSize:   '0.75rem',    // 12px
      lineHeight: 1.5,
      color:      TEXT_SECONDARY,
    },
    overline: {
      fontWeight: 600,
      fontSize:   '0.6875rem',  // 11px
      lineHeight: 2,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: TEXT_SECONDARY,
    },
  },

  // ── Shape (border radii) ───────────────────────────────────────────────────
  // MUI v9 uses shape.borderRadius as the base unit.
  // We set base = 6px (was 4px default). Components multiply it:
  //   Input/Button: 6px (1×)
  //   Card/Paper:   10px (set via components override)
  //   Dialog:       14px (set via components override)
  shape: {
    borderRadius: 6,
  },

  // ── Shadows ────────────────────────────────────────────────────────────────
  // MUI default shadows are too strong for a field app on bright screens.
  // We soften by ~40%: reduce alpha and spread on elevations 1-4 (most used).
  // Elevations 5-24 keep proportional reduction.
  shadows: [
    'none',                                                           // 0
    '0px 1px 2px rgba(0,0,0,0.07)',                                  // 1 (Card default)
    '0px 2px 4px rgba(0,0,0,0.08)',                                  // 2
    '0px 3px 6px rgba(0,0,0,0.09)',                                  // 3
    '0px 4px 10px rgba(0,0,0,0.10)',                                 // 4 (Dialog)
    '0px 5px 14px rgba(0,0,0,0.10)',                                 // 5
    '0px 6px 16px rgba(0,0,0,0.11)',                                 // 6
    '0px 7px 18px rgba(0,0,0,0.11)',                                 // 7
    '0px 8px 20px rgba(0,0,0,0.12)',                                 // 8
    '0px 9px 22px rgba(0,0,0,0.12)',                                 // 9
    '0px 10px 24px rgba(0,0,0,0.12)',                                // 10
    '0px 11px 26px rgba(0,0,0,0.13)',                                // 11
    '0px 12px 28px rgba(0,0,0,0.13)',                                // 12
    '0px 13px 30px rgba(0,0,0,0.13)',                                // 13
    '0px 14px 32px rgba(0,0,0,0.13)',                                // 14
    '0px 15px 34px rgba(0,0,0,0.14)',                                // 15
    '0px 16px 36px rgba(0,0,0,0.14)',                                // 16
    '0px 17px 38px rgba(0,0,0,0.14)',                                // 17
    '0px 18px 40px rgba(0,0,0,0.14)',                                // 18
    '0px 19px 42px rgba(0,0,0,0.14)',                                // 19
    '0px 20px 44px rgba(0,0,0,0.14)',                                // 20
    '0px 21px 46px rgba(0,0,0,0.15)',                                // 21
    '0px 22px 48px rgba(0,0,0,0.15)',                                // 22
    '0px 23px 50px rgba(0,0,0,0.15)',                                // 23
    '0px 24px 52px rgba(0,0,0,0.15)',                                // 24
  ],

  // ── Spacing ────────────────────────────────────────────────────────────────
  // Base 8px — identical to current SCSS $space-1. Explicitly declared for clarity.
  spacing: 8,

  // ── Component overrides ────────────────────────────────────────────────────
  // Rule: use theme.spacing() for all spacing values.
  // Touch target minimum: 44px (WCAG + field-use requirement).
  components: {

    // ── MuiButton ────────────────────────────────────────────────────────────
    // Default: no elevation (flat) + touch target ≥ 44px.
    // The custom material/Button.js wraps this — overrides apply there too.
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 44,
          // Token md (8px) — Filipe F7 2026-05-11: padronizar todos os botões
          // como retângulos com bordas arredondadas (drop do pill 999 e
          // do shape.borderRadius base de 6).
          borderRadius: RADIUS.md,
          paddingTop:    theme.spacing(1.25),  // 10px
          paddingBottom: theme.spacing(1.25),  // 10px
          paddingLeft:   theme.spacing(2.5),   // 20px
          paddingRight:  theme.spacing(2.5),   // 20px
        }),
        sizeSmall: ({ theme }) => ({
          minHeight: 36,
          paddingTop:    theme.spacing(0.75),  // 6px
          paddingBottom: theme.spacing(0.75),
          paddingLeft:   theme.spacing(1.5),   // 12px
          paddingRight:  theme.spacing(1.5),
        }),
        sizeLarge: ({ theme }) => ({
          minHeight: 52,
          paddingTop:    theme.spacing(1.75),  // 14px
          paddingBottom: theme.spacing(1.75),
          paddingLeft:   theme.spacing(3.5),   // 28px
          paddingRight:  theme.spacing(3.5),
        }),
      },
    },

    // ── MuiIconButton ────────────────────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth:  44,
          minHeight: 44,
        },
        sizeSmall: {
          minWidth:  36,
          minHeight: 36,
        },
      },
    },

    // ── MuiTextField / MuiInputBase ──────────────────────────────────────────
    // Default: outlined variant, size "small" but with 44px touch target.
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size:    'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight:    44,
          borderRadius: theme.shape.borderRadius,
          backgroundColor: SURFACE_PAPER,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: BORDER_STRONG,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ZOETIS_ORANGE,
            borderWidth: 2,
          },
        }),
        notchedOutline: {
          borderColor: BORDER_DEFAULT,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: TEXT_HINT,
          '&.Mui-focused': {
            color: ZOETIS_ORANGE,
          },
        },
      },
    },

    // ── MuiCard ──────────────────────────────────────────────────────────────
    // Default: outlined (no shadow by default — cleaner on bright outdoor screens).
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS.lg,  // 12px — token lg pra stand-alone cards
          borderColor:  BORDER_DEFAULT,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(3),          // 24px (was 16px)
          '&:last-child': {
            paddingBottom: theme.spacing(3),
          },
        }),
      },
    },

    // ── MuiDialog / MuiDialogContent ─────────────────────────────────────────
    MuiDialog: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 4,
          },
        },
      },
      styleOverrides: {
        paper: {
          borderRadius: RADIUS.xl,  // 16px — token xl pra dialog/modal
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding:    `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(1.5)}`,
          color:      TEXT_PRIMARY,
          fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
          fontWeight: 600,
          fontSize:   '1.125rem',
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${theme.spacing(1.5)} ${theme.spacing(3)} ${theme.spacing(2.5)}`,
          gap:     theme.spacing(1),
        }),
      },
    },

    // ── MuiMenuItem / MuiListItem ─────────────────────────────────────────────
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight:     44,
          paddingTop:    theme.spacing(1.25),
          paddingBottom: theme.spacing(1.25),
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
      },
    },

    // ── MuiTableCell ─────────────────────────────────────────────────────────
    // Line-height 1.2 → 1.45, padding 8×16 → 10×16.
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding:    `${theme.spacing(1.25)} ${theme.spacing(2)}`,  // 10px 16px
          lineHeight: 1.45,
          borderColor: BORDER_DEFAULT,
        }),
        head: {
          fontWeight:      600,
          backgroundColor: SURFACE_SUBTLE,
          color:           TEXT_PRIMARY,
          borderBottom:    `2px solid ${BORDER_STRONG}`,
        },
      },
    },

    // ── MuiTablePagination ────────────────────────────────────────────────────
    // Footer destacado em warm peach (#FDEDE2 = primary @ ~12% sobre off-white)
    // — sutil mas distingue claramente a zona de paginação do corpo da tabela.
    // Toolbar compactada (min-height 40px, era ~52px default) para reduzir
    // peso visual e ganhar espaço útil pra mais rows.
    MuiTablePagination: {
      styleOverrides: {
        root: {
          // Sem borderTop (removido 2026-05-10) — visual cleaner; o contraste
          // do bg warm peach já distingue a zona de paginação do corpo.
          backgroundColor: '#FDEDE2',
        },
        toolbar: {
          minHeight: 40,
          paddingLeft: 12,
          paddingRight: 8,
        },
        selectLabel: {
          fontSize: '0.8125rem',
          fontWeight: 500,
          marginBottom: 0,
        },
        displayedRows: {
          fontSize: '0.8125rem',
          marginBottom: 0,
        },
        select: {
          fontSize: '0.8125rem',
          paddingTop: 4,
          paddingBottom: 4,
        },
        actions: {
          marginLeft: 8,
          '& .MuiIconButton-root': {
            minWidth: 32,
            minHeight: 32,
            padding: 4,
          },
        },
      },
    },

    // ── MuiDivider ────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: BORDER_DEFAULT,
        },
      },
    },

    // ── MuiPaper ──────────────────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: BORDER_DEFAULT,
        },
        rounded: {
          borderRadius: RADIUS.md,  // 8px — token md, base pra containers
        },
      },
    },

    // ── MuiTableContainer ─────────────────────────────────────────────────────
    // Match com tokens — containers de tabela ganham radius md + borda padrão.
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.md,
          border: `1px solid ${BORDER_DEFAULT}`,
        },
      },
    },

    // ── MuiBreadcrumbs ────────────────────────────────────────────────────────
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: TEXT_DISABLED,
        },
      },
    },

    // ── MuiTooltip ────────────────────────────────────────────────────────────
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2A2A2A',
          fontSize:        '0.75rem',
        },
        arrow: {
          color: '#2A2A2A',
        },
      },
    },

    // ── MuiChip ───────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize:   '0.8125rem',
        },
      },
    },

    // ── MuiLinearProgress / MuiCircularProgress ───────────────────────────────
    // These already pick up primary.main — no overrides needed.

    // ── MuiAppBar (used nowhere directly but here for completeness) ───────────
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'default',
      },
      styleOverrides: {
        root: {
          backgroundColor: SURFACE_PAPER,
          borderBottom:    `1px solid ${BORDER_DEFAULT}`,
        },
      },
    },
  },
})

// ─── SEMANTIC TOKEN EXPORTS ───────────────────────────────────────────────────
// Exported as named constants so any component can import without importing
// the whole theme. Useful for SVG colors and inline cases that can't use sx.
export const tokens = {
  surface: {
    default: SURFACE_DEFAULT,
    paper:   SURFACE_PAPER,
    subtle:  SURFACE_SUBTLE,
  },
  border: {
    default: BORDER_DEFAULT,
    strong:  BORDER_STRONG,
  },
  text: {
    primary:   TEXT_PRIMARY,
    secondary: TEXT_SECONDARY,
    disabled:  TEXT_DISABLED,
    hint:      TEXT_HINT,
  },
  semantic: {
    success: { main: SEMANTIC_SUCCESS_MAIN, light: SEMANTIC_SUCCESS_LIGHT },
    warning: { main: SEMANTIC_WARNING_MAIN, light: SEMANTIC_WARNING_LIGHT },
    error:   { main: SEMANTIC_ERROR_MAIN,   light: SEMANTIC_ERROR_LIGHT   },
    info:    { main: SEMANTIC_INFO_MAIN,    light: SEMANTIC_INFO_LIGHT    },
  },
  brand: {
    orange: ZOETIS_ORANGE,
  },
  radius: RADIUS,

  // ─── Spacing scale (4-based) ───────────────────────────────────────────────
  // Use para sx values e em contextos fora-de-MUI (SCSS via colors.scss mirror).
  // sx value = px / 8. Exemplo: tokens.space.md = 16 = sx 2.
  space: {
    xxs:   4,
    xs:    8,
    sm:   12,
    md:   16,
    lg:   24,
    xl:   32,
    '2xl': 48,
  },
}

// Export direto da escala de radius — uso comum o suficiente pra justificar
// import nomeado em vez de tokens.radius.md em cada arquivo.
export const radius = RADIUS

export default appTheme
